import React from 'react';
import { igCss } from './styles';
import { GeneratedSocialPostResult } from 'src/api/types/social-types';

type Maybe<T> = T | null | undefined;

export interface IgCounters {
  likes?: number;
  comments?: number;
  shares?: number; // IG calls it “sends”; we’ll display the number next to the paper-plane
}

export interface InstagramPostPreviewProps {
  /** Use the same structure you pass to Facebook */
  data: GeneratedSocialPostResult;
  /** Pass precomputed counters (optional) */
  counters?: IgCounters;
  /** Inject CSS from stylesInstagram.ts (set false if you add it globally) */
  injectCss?: boolean;
  /** Profile header */
  profileName?: string;
  profileAvatarUrl?: string;
  when?: string; // e.g., "Just now"
}

/** Turns plain URLs into links inside the caption text */
function linkify(text: string) {
  const urlRegex =
    /((https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s]*)?)/gi;

  const lines = (text || '').split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line.split(urlRegex).map((chunk, j) => {
        const looksLikeUrl =
          /^https?:\/\//i.test(chunk) || /^[a-z0-9-]+\.[a-z]{2,}/i.test(chunk);
        if (looksLikeUrl) {
          const href = /^https?:\/\//i.test(chunk) ? chunk : `https://${chunk}`;
          return (
            <a
              key={`${i}-${j}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="igp-link">
              {chunk}
            </a>
          );
        }
        return <React.Fragment key={`${i}-${j}`}>{chunk}</React.Fragment>;
      })}
      {i < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
}

export default function InstagramPostPreview({
  data,
  counters,
  injectCss = true,
  profileName,
  profileAvatarUrl = 'https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg',
  when = 'Just now',
}: InstagramPostPreviewProps) {
  const post = data.post;

  const likeCount = counters?.likes ?? 0;
  const commentCount = counters?.comments ?? 0;
  const shareCount = counters?.shares ?? 0;

  // Image sources: prefer hosted link URL
  const imageSrc: Maybe<string> = data.imageLinkUrl || post.image?.link_url;
  const hasImage = Boolean(imageSrc);
  const headerName = profileName || post.location?.name || 'Profile Name';

  return (
    <>
      {injectCss && <style>{igCss}</style>}

      <main className="igp-container" role="main">
        <article className="igp-card" aria-label="Instagram-like post preview">
          {/* Header */}
          <header className="igp-header">
            <img src={profileAvatarUrl} alt="Profile" className="igp-avatar" />
            <div className="igp-header-main">
              <div className="igp-name-row">
                <a className="igp-name" role="button" tabIndex={0}>
                  {headerName}
                </a>
                <div className="igp-dot-time">
                  <b> · {when}</b>
                </div>
              </div>
            </div>
            <button aria-label="More" className="igp-more" type="button">
              <svg
                viewBox="0 0 24 24"
                className="igp-more-icon"
                aria-hidden="true">
                <circle cx="5" cy="12" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
            </button>
          </header>

          {/* Media */}
          {hasImage && (
            <section className="igp-media-wrap">
              <img
                src={imageSrc}
                alt={post.image?.alt || ''}
                className="igp-media"
              />
            </section>
          )}

          {/* Actions */}
          <div className="igp-actions">
            <div className="igp-actions-left">
              {/* Like */}
              <button
                id="btn-like"
                className="igp-action"
                aria-pressed="false"
                aria-label="Like"
                type="button">
                <svg
                  aria-label="Like"
                  fill="currentColor"
                  height="24"
                  role="img"
                  viewBox="0 0 24 24"
                  width="24">
                  <path d="M16.792 3.904A4.989 4.989 0 0 1 21.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 0 1 4.708-5.218 4.21 4.21 0 0 1 3.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 0 1 3.679-1.938m0-2a6.04 6.04 0 0 0-4.797 2.127 6.052 6.052 0 0 0-4.787-2.127A6.985 6.985 0 0 0 .5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 0 0 3.518 3.018 2 2 0 0 0 2.174 0 45.263 45.263 0 0 0 3.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 0 0-6.708-7.218Z"></path>
                </svg>
                <p className="igp-action-count">{likeCount}</p>
              </button>

              {/* Comment */}
              <button
                id="btn-comment"
                className="igp-action"
                aria-label="Comment"
                type="button">
                <svg
                  aria-label="Comment"
                  fill="currentColor"
                  height="24"
                  role="img"
                  viewBox="0 0 24 24"
                  width="24">
                  <path
                    d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z"
                    fill="none"
                    stroke="currentColor"
                    strokeLinejoin="round"
                    strokeWidth="2"></path>
                </svg>
                <p className="igp-action-count">{commentCount}</p>
              </button>

              {/* Send */}
              <button
                id="btn-send"
                className="igp-action"
                aria-label="Share"
                type="button">
                <svg
                  aria-label="Share"
                  fill="currentColor"
                  height="24"
                  role="img"
                  viewBox="0 0 24 24"
                  width="24">
                  <line
                    fill="none"
                    stroke="currentColor"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    x1="22"
                    x2="9.218"
                    y1="3"
                    y2="10.083"></line>
                  <polygon
                    fill="none"
                    points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334"
                    stroke="currentColor"
                    strokeLinejoin="round"
                    strokeWidth="2"></polygon>
                </svg>
                <span className="igp-action-count">{shareCount}</span>
              </button>
            </div>

            {/* Save */}
            <button
              id="btn-save"
              className="igp-save"
              aria-label="Save"
              type="button">
              <svg
                aria-label="Save"
                fill="currentColor"
                height="24"
                role="img"
                viewBox="0 0 24 24"
                width="24">
                <polygon
                  fill="none"
                  points="20 21 12 13.44 4 21 4 3 20 3 20 21"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"></polygon>
              </svg>
            </button>
          </div>

          {/* Likes */}
          <div className="igp-likes">
            <b>{Intl.NumberFormat().format(likeCount)} likes</b>
          </div>

          {/* Caption */}
          <div className="igp-caption">
            <span className="igp-caption-name">
              <b>{headerName.toLowerCase().replace(/\s+/g, '')}</b>
            </span>
            <span className="igp-caption-text">
              {' '}
              {linkify(post.body || '')}
            </span>
          </div>

          {/* Hashtags */}
          {post.hashtags?.length ? (
            <div className="igp-tags" aria-label="Hashtags">
              {post.hashtags.map(tag => (
                <a key={tag} className="igp-tag">
                  {tag}
                </a>
              ))}
            </div>
          ) : null}

          {/* Link note: IG doesn’t render link previews in the feed */}
          {post.metadata?.has_link && post.cta?.url ? (
            <div className="igp-link-note">{post.cta.url}</div>
          ) : null}

          {/* View comments (static preview) */}
          <button className="igp-view-comments" type="button">
            View all {Math.max(commentCount, 1)} comments
          </button>

          {/* Timestamp */}
          <div className="igp-timestamp">{when}</div>
        </article>
      </main>
    </>
  );
}
